import { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import {
  Mail,
  MessageSquare,
  Bell,
  Server,
  Lock,
  User,
  AtSign,
  Save,
  RefreshCw,
  Send,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Eye,
  EyeOff,
  Phone,
  Key,
  FileText,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  Info,
  Copy,
  Palette,
  Type,
  MessageCircle,
  Wifi,
  Radio,
  Code,
  Upload,
} from 'lucide-react';
import { adminApi } from '../../services/api';
import clsx from 'clsx';

// ============================================================================
// Types
// ============================================================================

interface EmailSettings {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  from: string;
  enabled: boolean;
  hasPassword: boolean;
}

interface TwilioSettings {
  accountSid: string;
  fromNumber: string;
  senderId: string;
  enabled: boolean;
  hasAuthToken: boolean;
}

interface FirebaseSettings {
  projectId: string;
  clientEmail: string;
  apiKey: string;
  messagingSenderId: string;
  appId: string;
  vapidKey: string;
  enabled: boolean;
  hasPrivateKey: boolean;
}

// ============================================================================
// Email Tab Component
// ============================================================================

function EmailTab() {
  const [settings, setSettings] = useState<EmailSettings | null>(null);
  const [usingEnvVars, setUsingEnvVars] = useState(false);
  const [envConfigured, setEnvConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; error?: string } | null>(null);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [formData, setFormData] = useState({
    host: '',
    port: 587,
    secure: false,
    user: '',
    password: '',
    from: '',
    enabled: true,
  });

  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [showTestModal, setShowTestModal] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await adminApi.getEmailSettings();
      const data = res.data;

      setSettings(data.settings);
      setUsingEnvVars(data.usingEnvVars);
      setEnvConfigured(data.envConfigured);

      if (data.settings) {
        setFormData({
          host: data.settings.host || '',
          port: data.settings.port || 587,
          secure: data.settings.secure || false,
          user: data.settings.user || '',
          password: '',
          from: data.settings.from || '',
          enabled: data.settings.enabled ?? true,
        });
      }
    } catch (error) {
      console.error('Failed to load email settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveMessage(null);

    try {
      await adminApi.updateEmailSettings(formData);
      setSaveMessage({ type: 'success', text: 'Paramètres enregistrés avec succès' });
      await loadSettings();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      setSaveMessage({
        type: 'error',
        text: err.response?.data?.error || 'Erreur lors de l\'enregistrement',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const res = await adminApi.testEmailConnection(formData);
      setTestResult(res.data);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      setTestResult({
        success: false,
        error: err.response?.data?.error || 'Échec du test de connexion',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailAddress) return;

    setSendingTest(true);
    try {
      await adminApi.sendTestEmail({ to: testEmailAddress });
      alert('Email de test envoyé avec succès !');
      setShowTestModal(false);
      setTestEmailAddress('');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      alert(err.response?.data?.error || 'Erreur lors de l\'envoi');
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Banner */}
      {usingEnvVars && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-blue-500" />
            <div>
              <p className="text-blue-800 font-medium">Variables d'environnement actives</p>
              <p className="text-blue-600 text-sm">
                La configuration email utilise actuellement les variables d'environnement.
              </p>
            </div>
          </div>
        </div>
      )}

      {!settings && !envConfigured && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            <div>
              <p className="text-yellow-800 font-medium">Email non configure</p>
              <p className="text-yellow-600 text-sm">
                Configurez les parametres SMTP ci-dessous pour activer l'envoi d'emails.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Settings Form */}
      <form onSubmit={handleSave} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* SMTP Host */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              <Server className="h-4 w-4 inline mr-2" />
              Serveur SMTP
            </label>
            <input
              type="text"
              value={formData.host}
              onChange={(e) => setFormData({ ...formData, host: e.target.value })}
              placeholder="smtp.example.com"
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
              required
            />
          </div>

          {/* SMTP Port */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">Port</label>
            <select
              value={formData.port}
              onChange={(e) => {
                const port = parseInt(e.target.value);
                setFormData({
                  ...formData,
                  port,
                  secure: port === 465,
                });
              }}
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
            >
              <option value={587}>587 (TLS/STARTTLS)</option>
              <option value={465}>465 (SSL)</option>
              <option value={25}>25 (Sans chiffrement)</option>
              <option value={2525}>2525 (Alternatif)</option>
            </select>
          </div>

          {/* Username */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              <User className="h-4 w-4 inline mr-2" />
              Nom d'utilisateur
            </label>
            <input
              type="text"
              value={formData.user}
              onChange={(e) => setFormData({ ...formData, user: e.target.value })}
              placeholder="user@example.com"
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
              required
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              <Lock className="h-4 w-4 inline mr-2" />
              Mot de passe
              {settings?.hasPassword && (
                <span className="ml-2 text-xs text-gray-400">(laisser vide pour conserver)</span>
              )}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={settings?.hasPassword ? '••••••••' : 'Entrer le mot de passe'}
                className="w-full px-4 py-2 pr-10 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* From Address */}
          <div className="md:col-span-2">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              <AtSign className="h-4 w-4 inline mr-2" />
              Adresse d'expedition
            </label>
            <input
              type="email"
              value={formData.from}
              onChange={(e) => setFormData({ ...formData, from: e.target.value })}
              placeholder="noreply@trackfleet.lu"
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
              required
            />
          </div>

          {/* Enable/Disable */}
          <div className="md:col-span-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                className="w-5 h-5 rounded border-gray-300 text-cyan-500 focus:ring-cyan-500"
              />
              <span className="text-gray-700 font-medium">Activer l'envoi d'emails</span>
            </label>
          </div>
        </div>

        {/* Connection Test Result */}
        {testResult && (
          <div
            className={`mt-6 p-4 rounded-lg ${
              testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            }`}
          >
            <div className="flex items-center gap-3">
              {testResult.success ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500" />
              )}
              <div>
                <p className={testResult.success ? 'text-green-800' : 'text-red-800'}>
                  {testResult.success ? 'Connexion réussie !' : 'Échec de la connexion'}
                </p>
                {testResult.error && <p className="text-red-600 text-sm mt-1">{testResult.error}</p>}
              </div>
            </div>
          </div>
        )}

        {/* Save Message */}
        {saveMessage && (
          <div
            className={`mt-6 p-4 rounded-lg ${
              saveMessage.type === 'success'
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}
          >
            <p className={saveMessage.type === 'success' ? 'text-green-800' : 'text-red-800'}>
              {saveMessage.text}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handleTestConnection}
            disabled={testing || !formData.host || !formData.user}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {testing ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Tester la connexion
          </button>

          <button
            type="button"
            onClick={() => setShowTestModal(true)}
            disabled={!settings?.hasPassword && !formData.password}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="h-4 w-4" />
            Envoyer un email de test
          </button>

          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors flex items-center gap-2 ml-auto disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Enregistrer
          </button>
        </div>
      </form>

      {/* Test Email Modal */}
      {showTestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md m-4">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Envoyer un email de test</h2>
            </div>
            <div className="p-6">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Adresse du destinataire
              </label>
              <input
                type="email"
                value={testEmailAddress}
                onChange={(e) => setTestEmailAddress(e.target.value)}
                placeholder="test@example.com"
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
              />
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowTestModal(false)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSendTestEmail}
                disabled={sendingTest || !testEmailAddress}
                className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingTest ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Envoyer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Twilio Tab Component
// ============================================================================

function TwilioTab() {
  const [settings, setSettings] = useState<TwilioSettings | null>(null);
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAuthToken, setShowAuthToken] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [formData, setFormData] = useState({
    accountSid: '',
    authToken: '',
    fromNumber: '',
    senderId: '',
    enabled: true,
  });

  const [testPhoneNumber, setTestPhoneNumber] = useState('');
  const [showTestModal, setShowTestModal] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await adminApi.getTwilioSettings();
      const data = res.data;

      setSettings(data.settings);
      setConfigured(data.configured);

      if (data.settings) {
        setFormData({
          accountSid: data.settings.accountSid || '',
          authToken: '',
          fromNumber: data.settings.fromNumber || '',
          senderId: data.settings.senderId || '',
          enabled: data.settings.enabled ?? true,
        });
      }
    } catch (error) {
      console.error('Failed to load Twilio settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveMessage(null);

    try {
      await adminApi.updateTwilioSettings(formData);
      setSaveMessage({ type: 'success', text: 'Paramètres Twilio enregistrés avec succès' });
      await loadSettings();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      setSaveMessage({
        type: 'error',
        text: err.response?.data?.error || 'Erreur lors de l\'enregistrement',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestSms = async () => {
    if (!testPhoneNumber) return;

    setSendingTest(true);
    try {
      await adminApi.testTwilioSms({ to: testPhoneNumber });
      alert('SMS de test envoyé avec succès !');
      setShowTestModal(false);
      setTestPhoneNumber('');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string; details?: string } } };
      alert(err.response?.data?.details || err.response?.data?.error || 'Erreur lors de l\'envoi');
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Banner */}
      {!configured && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            <div>
              <p className="text-yellow-800 font-medium">Twilio non configure</p>
              <p className="text-yellow-600 text-sm">
                Configurez vos identifiants Twilio pour activer l'envoi de SMS.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Settings Form */}
      <form onSubmit={handleSave} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Account SID */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              <Key className="h-4 w-4 inline mr-2" />
              Account SID
            </label>
            <input
              type="text"
              value={formData.accountSid}
              onChange={(e) => setFormData({ ...formData, accountSid: e.target.value })}
              placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
              required
            />
          </div>

          {/* Auth Token */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              <Lock className="h-4 w-4 inline mr-2" />
              Auth Token
              {settings?.hasAuthToken && (
                <span className="ml-2 text-xs text-gray-400">(laisser vide pour conserver)</span>
              )}
            </label>
            <div className="relative">
              <input
                type={showAuthToken ? 'text' : 'password'}
                value={formData.authToken}
                onChange={(e) => setFormData({ ...formData, authToken: e.target.value })}
                placeholder={settings?.hasAuthToken ? '••••••••' : 'Entrer le Auth Token'}
                className="w-full px-4 py-2 pr-10 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
              />
              <button
                type="button"
                onClick={() => setShowAuthToken(!showAuthToken)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showAuthToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* From Number */}
          <div className="md:col-span-2">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              <Phone className="h-4 w-4 inline mr-2" />
              Numéro d'expéditeur
            </label>
            <input
              type="text"
              value={formData.fromNumber}
              onChange={(e) => setFormData({ ...formData, fromNumber: e.target.value })}
              placeholder="+33612345678"
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Numéro de téléphone Twilio au format international (ex: +33612345678)
            </p>
          </div>

          {/* Alphanumeric Sender ID */}
          <div className="md:col-span-2">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Nom expéditeur (optionnel)
            </label>
            <input
              type="text"
              value={formData.senderId}
              onChange={(e) => setFormData({ ...formData, senderId: e.target.value })}
              placeholder="MaSociete"
              maxLength={11}
              pattern="[A-Za-z0-9 ]*[A-Za-z][A-Za-z0-9 ]*"
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
            />
            <p className="mt-1 text-xs text-gray-500">
              11 caractères max (lettres, chiffres, espaces — au moins une lettre).
              Si renseigné, les destinataires verront ce nom à la place du numéro.
              Non supporté aux États-Unis/Canada ; certains pays (FR, UK…) exigent
              un pré-enregistrement auprès de Twilio. Les destinataires ne pourront
              pas répondre.
            </p>
          </div>

          {/* Enable/Disable */}
          <div className="md:col-span-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                className="w-5 h-5 rounded border-gray-300 text-cyan-500 focus:ring-cyan-500"
              />
              <span className="text-gray-700 font-medium">Activer l'envoi de SMS</span>
            </label>
          </div>
        </div>

        {/* Save Message */}
        {saveMessage && (
          <div
            className={`mt-6 p-4 rounded-lg ${
              saveMessage.type === 'success'
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}
          >
            <p className={saveMessage.type === 'success' ? 'text-green-800' : 'text-red-800'}>
              {saveMessage.text}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setShowTestModal(true)}
            disabled={!configured}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="h-4 w-4" />
            Envoyer un SMS de test
          </button>

          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors flex items-center gap-2 ml-auto disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Enregistrer
          </button>
        </div>
      </form>

      {/* Test SMS Modal */}
      {showTestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md m-4">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Envoyer un SMS de test</h2>
            </div>
            <div className="p-6">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Numéro du destinataire
              </label>
              <input
                type="tel"
                value={testPhoneNumber}
                onChange={(e) => setTestPhoneNumber(e.target.value)}
                placeholder="+33612345678"
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
              />
              <p className="mt-1 text-xs text-gray-500">
                Format international requis (ex: +33612345678)
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowTestModal(false)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSendTestSms}
                disabled={sendingTest || !testPhoneNumber}
                className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingTest ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Envoyer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Truphone Tab Component
// ============================================================================

interface TruphoneSettingsData {
  identifier: string;
  enabled: boolean;
  hasToken: boolean;
  hasPassword: boolean;
}

function TruphoneTab() {
  const [settings, setSettings] = useState<TruphoneSettingsData | null>(null);
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [formData, setFormData] = useState({
    token: '',
    identifier: '',
    password: '',
    enabled: true,
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await adminApi.getTruphoneSettings();
      const data = res.data;

      setSettings(data.settings);
      setConfigured(data.configured);

      if (data.settings) {
        setFormData({
          token: '',
          identifier: data.settings.identifier || '',
          password: '',
          enabled: data.settings.enabled ?? true,
        });
      }
    } catch (error) {
      console.error('Failed to load Truphone settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveMessage(null);

    try {
      await adminApi.updateTruphoneSettings(formData);
      setSaveMessage({ type: 'success', text: 'Paramètres Truphone enregistrés avec succès' });
      await loadSettings();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      setSaveMessage({
        type: 'error',
        text: err.response?.data?.error || 'Erreur lors de l\'enregistrement',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setVerifying(true);
    try {
      const res = await adminApi.testTruphoneConnection();
      alert(res.data.message || 'Connexion Truphone OK');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string; details?: string } } };
      alert(err.response?.data?.details || err.response?.data?.error || 'Erreur de connexion');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Banner */}
      {!configured && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            <div>
              <p className="text-yellow-800 font-medium">Truphone non configuré</p>
              <p className="text-yellow-600 text-sm">
                Configurez vos identifiants Truphone pour activer l'envoi de SMS IoT aux trackers.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Settings Form */}
      <form onSubmit={handleSave} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* API Token */}
          <div className="md:col-span-2">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              <Key className="h-4 w-4 inline mr-2" />
              Token API (Bearer)
            </label>
            <div className="relative">
              <input
                type={showToken ? 'text' : 'password'}
                value={formData.token}
                onChange={(e) => setFormData({ ...formData, token: e.target.value })}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 pr-10"
                placeholder={settings?.hasToken ? '••••••••••••' : 'Entrez le token API Truphone'}
              />
              <button
                type="button"
                onClick={() => setShowToken(!showToken)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {settings?.hasToken && (
              <p className="text-xs text-gray-500 mt-1">Un token est déjà configuré. Laissez vide pour le conserver.</p>
            )}
          </div>

          {/* Identifier */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              <User className="h-4 w-4 inline mr-2" />
              Identifiant SMS
            </label>
            <input
              type="text"
              value={formData.identifier}
              onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
              placeholder="admin"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Identifiant envoyé au tracker (ex: admin)</p>
          </div>

          {/* Password */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              <Lock className="h-4 w-4 inline mr-2" />
              Mot de passe SMS
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 pr-10"
                placeholder={settings?.hasPassword ? '••••••••••••' : 'admin123'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {settings?.hasPassword && (
              <p className="text-xs text-gray-500 mt-1">Un mot de passe est déjà configuré. Laissez vide pour le conserver.</p>
            )}
          </div>

          {/* Enable Toggle */}
          <div className="md:col-span-2 flex items-center gap-3 pt-2">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={formData.enabled}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500"></div>
            </label>
            <span className="text-sm text-gray-700">Activer l'envoi SMS via Truphone</span>
          </div>
        </div>

        {/* Info box */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-500 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">Format SMS Teltonika</p>
              <p>Les SMS sont envoyés au format : <code className="bg-blue-100 px-1 rounded">&lt;identifiant&gt; &lt;mot de passe&gt; &lt;commande&gt;</code></p>
              <p className="mt-1">Exemple : <code className="bg-blue-100 px-1 rounded">admin admin123 getinfo</code></p>
            </div>
          </div>
        </div>

        {/* Save Message */}
        {saveMessage && (
          <div className={clsx(
            'mt-4 p-3 rounded-lg flex items-center gap-2',
            saveMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          )}>
            {saveMessage.type === 'success' ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
            {saveMessage.text}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Enregistrer
          </button>
          <button
            type="button"
            onClick={handleTestConnection}
            disabled={!configured || verifying}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {verifying ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Wifi className="h-4 w-4" />}
            Tester la connexion
          </button>
        </div>
      </form>
    </div>
  );
}

// ============================================================================
// FOTA Web Tab Component
// ============================================================================

interface FotaWebSettingsData {
  apiUrl: string;
  enabled: boolean;
  hasApiToken: boolean;
}

function FotaWebTab() {
  const [settings, setSettings] = useState<FotaWebSettingsData | null>(null);
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [formData, setFormData] = useState({
    apiToken: '',
    apiUrl: 'https://api.teltonika.lt',
    enabled: true,
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await adminApi.getFotaWebSettings();
      const data = res.data;

      setSettings(data.settings);
      setConfigured(data.configured);

      if (data.settings) {
        setFormData({
          apiToken: '',
          apiUrl: data.settings.apiUrl || 'https://api.teltonika.lt',
          enabled: data.settings.enabled ?? true,
        });
      }
    } catch (error) {
      console.error('Failed to load FOTA Web settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveMessage(null);

    try {
      await adminApi.updateFotaWebSettings(formData);
      setSaveMessage({ type: 'success', text: 'Paramètres FOTA Web enregistrés avec succès' });
      await loadSettings();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      setSaveMessage({
        type: 'error',
        text: err.response?.data?.error || 'Erreur lors de l\'enregistrement',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setVerifying(true);
    try {
      const res = await adminApi.testFotaWebConnection();
      alert(res.data.message || 'Connexion FOTA Web OK');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string; details?: string } } };
      alert(err.response?.data?.details || err.response?.data?.error || 'Erreur de connexion');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Banner */}
      {!configured && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            <div>
              <p className="text-yellow-800 font-medium">FOTA Web non configuré</p>
              <p className="text-yellow-600 text-sm">
                Configurez votre token API FOTA Web pour activer l'envoi de configurations aux trackers Teltonika.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Settings Form */}
      <form onSubmit={handleSave} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* API Token */}
          <div className="md:col-span-2">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              <Key className="h-4 w-4 inline mr-2" />
              Token API FOTA Web
            </label>
            <div className="relative">
              <input
                type={showToken ? 'text' : 'password'}
                value={formData.apiToken}
                onChange={(e) => setFormData({ ...formData, apiToken: e.target.value })}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 pr-10"
                placeholder={settings?.hasApiToken ? '••••••••••••' : 'Entrez le token API FOTA Web'}
              />
              <button
                type="button"
                onClick={() => setShowToken(!showToken)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {settings?.hasApiToken && (
              <p className="text-xs text-gray-500 mt-1">Un token est déjà configuré. Laissez vide pour le conserver.</p>
            )}
          </div>

          {/* API URL */}
          <div className="md:col-span-2">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              <Server className="h-4 w-4 inline mr-2" />
              URL de l'API FOTA Web
            </label>
            <input
              type="url"
              value={formData.apiUrl}
              onChange={(e) => setFormData({ ...formData, apiUrl: e.target.value })}
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
              placeholder="https://api.teltonika.lt"
            />
            <p className="text-xs text-gray-500 mt-1">URL par défaut : https://api.teltonika.lt</p>
          </div>

          {/* Enable Toggle */}
          <div className="md:col-span-2 flex items-center gap-3 pt-2">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={formData.enabled}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500"></div>
            </label>
            <span className="text-sm text-gray-700">Activer l'intégration FOTA Web</span>
          </div>
        </div>

        {/* Info box */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-500 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">FOTA Web - Teltonika</p>
              <p>Permet d'envoyer des fichiers de configuration (.cfg) aux trackers Teltonika à distance via la plateforme FOTA Web.</p>
              <p className="mt-1">Pour générer un token : connectez-vous sur <code className="bg-blue-100 px-1 rounded">fota.teltonika.lt</code> &rarr; Settings &rarr; API Tokens &rarr; Generate.</p>
              <p className="mt-1 text-blue-600 font-medium">Le token n'est affiché qu'une seule fois lors de sa création. Conservez-le en lieu sûr.</p>
            </div>
          </div>
        </div>

        {/* Save Message */}
        {saveMessage && (
          <div className={clsx(
            'mt-4 p-3 rounded-lg flex items-center gap-2',
            saveMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          )}>
            {saveMessage.type === 'success' ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
            {saveMessage.text}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Enregistrer
          </button>
          <button
            type="button"
            onClick={handleTestConnection}
            disabled={!configured || verifying}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {verifying ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Wifi className="h-4 w-4" />}
            Tester la connexion
          </button>
        </div>
      </form>
    </div>
  );
}

// ============================================================================
// Firebase Tab Component
// ============================================================================

function FirebaseTab() {
  const [settings, setSettings] = useState<FirebaseSettings | null>(null);
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [formData, setFormData] = useState({
    projectId: '',
    privateKey: '',
    clientEmail: '',
    apiKey: '',
    messagingSenderId: '',
    appId: '',
    vapidKey: '',
    enabled: true,
  });

  const [testToken, setTestToken] = useState('');
  const [showTestModal, setShowTestModal] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadMessage, setUploadMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleJsonUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target?.result as string);

        setFormData((prev) => ({
          ...prev,
          ...(json.project_id ? { projectId: json.project_id } : {}),
          ...(json.client_email ? { clientEmail: json.client_email } : {}),
          ...(json.private_key ? { privateKey: json.private_key } : {}),
        }));

        const filled: string[] = [];
        if (json.project_id) filled.push('Project ID');
        if (json.client_email) filled.push('Client Email');
        if (json.private_key) filled.push('Private Key');

        if (filled.length > 0) {
          setUploadMessage({ type: 'success', text: `Champs remplis : ${filled.join(', ')}` });
        } else {
          setUploadMessage({ type: 'error', text: 'Aucun champ reconnu dans ce fichier JSON' });
        }
      } catch {
        setUploadMessage({ type: 'error', text: 'Fichier JSON invalide' });
      }

      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
    };
    reader.readAsText(file);
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await adminApi.getFirebaseSettings();
      const data = res.data;

      setSettings(data.settings);
      setConfigured(data.configured);

      if (data.settings) {
        setFormData({
          projectId: data.settings.projectId || '',
          privateKey: '',
          clientEmail: data.settings.clientEmail || '',
          apiKey: data.settings.apiKey || '',
          messagingSenderId: data.settings.messagingSenderId || '',
          appId: data.settings.appId || '',
          vapidKey: data.settings.vapidKey || '',
          enabled: data.settings.enabled ?? true,
        });
      }
    } catch (error) {
      console.error('Failed to load Firebase settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveMessage(null);

    try {
      await adminApi.updateFirebaseSettings(formData);
      setSaveMessage({ type: 'success', text: 'Paramètres Firebase enregistrés avec succès' });
      await loadSettings();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      setSaveMessage({
        type: 'error',
        text: err.response?.data?.error || 'Erreur lors de l\'enregistrement',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestPush = async () => {
    if (!testToken) return;

    setSendingTest(true);
    try {
      await adminApi.testFirebasePush({ token: testToken });
      alert('Notification push de test envoyée avec succès !');
      setShowTestModal(false);
      setTestToken('');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string; details?: string } } };
      alert(err.response?.data?.details || err.response?.data?.error || 'Erreur lors de l\'envoi');
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Banner */}
      {!configured && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            <div>
              <p className="text-yellow-800 font-medium">Firebase non configure</p>
              <p className="text-yellow-600 text-sm">
                Configurez vos identifiants Firebase pour activer les notifications push.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Settings Form */}
      <form onSubmit={handleSave} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Project ID */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              <Key className="h-4 w-4 inline mr-2" />
              Project ID
            </label>
            <input
              type="text"
              value={formData.projectId}
              onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
              placeholder="my-project-123456"
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
              required
            />
          </div>

          {/* Client Email */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              <AtSign className="h-4 w-4 inline mr-2" />
              Client Email
            </label>
            <input
              type="email"
              value={formData.clientEmail}
              onChange={(e) => setFormData({ ...formData, clientEmail: e.target.value })}
              placeholder="firebase-adminsdk@project.iam.gserviceaccount.com"
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
              required
            />
          </div>

          {/* Section: Client SDK Configuration */}
          <div className="md:col-span-2 pt-4 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Configuration SDK Client (pour recevoir les notifications)
            </h3>
          </div>

          {/* API Key */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              API Key
            </label>
            <input
              type="text"
              value={formData.apiKey}
              onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
              placeholder="AIzaSy..."
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Cle API Web (Firebase Console → Project Settings → General)
            </p>
          </div>

          {/* Messaging Sender ID */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Messaging Sender ID
            </label>
            <input
              type="text"
              value={formData.messagingSenderId}
              onChange={(e) => setFormData({ ...formData, messagingSenderId: e.target.value })}
              placeholder="123456789012"
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              ID de l'expéditeur (Firebase Console → Project Settings → Cloud Messaging)
            </p>
          </div>

          {/* App ID */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              App ID
            </label>
            <input
              type="text"
              value={formData.appId}
              onChange={(e) => setFormData({ ...formData, appId: e.target.value })}
              placeholder="1:123456789012:web:abc123def456"
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              ID de l'application Web (Firebase Console → Project Settings → Your Apps)
            </p>
          </div>

          {/* VAPID Key */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              VAPID Key (Web Push Certificate)
            </label>
            <input
              type="text"
              value={formData.vapidKey}
              onChange={(e) => setFormData({ ...formData, vapidKey: e.target.value })}
              placeholder="BNx..."
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Cle publique VAPID (Firebase Console → Project Settings → Cloud Messaging → Web Push certificates)
            </p>
          </div>

          {/* Section: Service Account */}
          <div className="md:col-span-2 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Service Account (pour envoyer les notifications)
              </h3>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,application/json"
                  onChange={handleJsonUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-2 text-sm"
                >
                  <Upload className="h-4 w-4" />
                  Importer le fichier JSON
                </button>
              </div>
            </div>
            {uploadMessage && (
              <div
                className={`mb-4 p-3 rounded-lg text-sm ${
                  uploadMessage.type === 'success'
                    ? 'bg-green-50 border border-green-200 text-green-800'
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}
              >
                {uploadMessage.text}
              </div>
            )}
          </div>

          {/* Private Key */}
          <div className="md:col-span-2">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              <FileText className="h-4 w-4 inline mr-2" />
              Private Key
              {settings?.hasPrivateKey && (
                <span className="ml-2 text-xs text-gray-400">(laisser vide pour conserver)</span>
              )}
            </label>
            <div className="relative">
              <textarea
                value={formData.privateKey}
                onChange={(e) => setFormData({ ...formData, privateKey: e.target.value })}
                placeholder={settings?.hasPrivateKey ? 'Cle privee configuree' : '-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----'}
                rows={4}
                className={clsx(
                  'w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20 font-mono text-sm',
                  !showPrivateKey && formData.privateKey && 'text-security-disc'
                )}
                style={!showPrivateKey && formData.privateKey ? { WebkitTextSecurity: 'disc' } as React.CSSProperties : undefined}
              />
              <button
                type="button"
                onClick={() => setShowPrivateKey(!showPrivateKey)}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
              >
                {showPrivateKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Cle privee du compte de service Firebase (depuis le fichier JSON)
            </p>
          </div>

          {/* Enable/Disable */}
          <div className="md:col-span-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                className="w-5 h-5 rounded border-gray-300 text-cyan-500 focus:ring-cyan-500"
              />
              <span className="text-gray-700 font-medium">Activer les notifications push</span>
            </label>
          </div>
        </div>

        {/* Save Message */}
        {saveMessage && (
          <div
            className={`mt-6 p-4 rounded-lg ${
              saveMessage.type === 'success'
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}
          >
            <p className={saveMessage.type === 'success' ? 'text-green-800' : 'text-red-800'}>
              {saveMessage.text}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setShowTestModal(true)}
            disabled={!configured}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="h-4 w-4" />
            Envoyer une notification de test
          </button>

          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors flex items-center gap-2 ml-auto disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Enregistrer
          </button>
        </div>
      </form>

      {/* Test Push Modal */}
      {showTestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md m-4">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Envoyer une notification de test</h2>
            </div>
            <div className="p-6">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                FCM Token du destinataire
              </label>
              <textarea
                value={testToken}
                onChange={(e) => setTestToken(e.target.value)}
                placeholder="dxxxxxxxxxxxxxxxxxxxxxxxxx..."
                rows={3}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20 font-mono text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                Token FCM de l'appareil cible (disponible dans la console Firebase ou l'app mobile)
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowTestModal(false)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSendTestPush}
                disabled={sendingTest || !testToken}
                className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingTest ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Envoyer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// WhatsApp Tab Component
// ============================================================================

interface WhatsAppSettings {
  phoneNumberId: string;
  businessAccountId: string;
  enabled: boolean;
  hasAccessToken: boolean;
}

function WhatsAppTab() {
  const [settings, setSettings] = useState<WhatsAppSettings | null>(null);
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAccessToken, setShowAccessToken] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [formData, setFormData] = useState({
    phoneNumberId: '',
    accessToken: '',
    businessAccountId: '',
    enabled: true,
  });

  const [testPhoneNumber, setTestPhoneNumber] = useState('');
  const [showTestModal, setShowTestModal] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<boolean | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await adminApi.getWhatsAppSettings();
      const data = res.data;

      setSettings(data.settings);
      setConfigured(data.configured);

      if (data.settings) {
        setFormData({
          phoneNumberId: data.settings.phoneNumberId || '',
          accessToken: '',
          businessAccountId: data.settings.businessAccountId || '',
          enabled: data.settings.enabled ?? true,
        });
      }
    } catch (error) {
      console.error('Failed to load WhatsApp settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveMessage(null);

    try {
      await adminApi.updateWhatsAppSettings(formData);
      setSaveMessage({ type: 'success', text: 'Parametres WhatsApp enregistres avec succes' });
      setConnectionStatus(null);
      await loadSettings();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      setSaveMessage({
        type: 'error',
        text: err.response?.data?.error || 'Erreur lors de l\'enregistrement',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    setConnectionStatus(null);
    try {
      const res = await adminApi.verifyWhatsApp();
      setConnectionStatus(res.data.connected);
    } catch {
      setConnectionStatus(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSendTest = async () => {
    if (!testPhoneNumber) return;

    setSendingTest(true);
    try {
      await adminApi.testWhatsApp({ to: testPhoneNumber });
      alert('Message WhatsApp de test envoye avec succes !');
      setShowTestModal(false);
      setTestPhoneNumber('');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string; details?: string } } };
      alert(err.response?.data?.details || err.response?.data?.error || 'Erreur lors de l\'envoi');
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Banner */}
      {!configured && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            <div>
              <p className="text-yellow-800 font-medium">WhatsApp non configure</p>
              <p className="text-yellow-600 text-sm">
                Configurez vos identifiants Meta Business pour activer l'envoi de messages WhatsApp.
              </p>
            </div>
          </div>
        </div>
      )}

      {configured && connectionStatus === true && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <p className="text-green-800 font-medium">Connexion WhatsApp verifiee avec succes</p>
          </div>
        </div>
      )}

      {connectionStatus === false && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <XCircle className="h-5 w-5 text-red-500" />
            <p className="text-red-800 font-medium">Echec de la connexion WhatsApp. Verifiez vos identifiants.</p>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-500 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Configuration Meta Business requise</p>
            <ol className="list-decimal list-inside space-y-1 text-blue-700">
              <li>Creez une app sur <span className="font-mono text-xs">developers.facebook.com</span></li>
              <li>Ajoutez le produit <strong>WhatsApp</strong></li>
              <li>Recuperez le <strong>Phone Number ID</strong>, <strong>Access Token</strong> et <strong>Business Account ID</strong></li>
              <li>Collez-les ci-dessous et enregistrez</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Settings Form */}
      <form onSubmit={handleSave} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Phone Number ID */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              <Phone className="h-4 w-4 inline mr-2" />
              Phone Number ID
            </label>
            <input
              type="text"
              value={formData.phoneNumberId}
              onChange={(e) => setFormData({ ...formData, phoneNumberId: e.target.value })}
              placeholder="123456789012345"
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Identifiant du numero WhatsApp Business (dans les parametres de l'app Meta)
            </p>
          </div>

          {/* Access Token */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              <Lock className="h-4 w-4 inline mr-2" />
              Access Token
              {settings?.hasAccessToken && (
                <span className="ml-2 text-xs text-gray-400">(laisser vide pour conserver)</span>
              )}
            </label>
            <div className="relative">
              <input
                type={showAccessToken ? 'text' : 'password'}
                value={formData.accessToken}
                onChange={(e) => setFormData({ ...formData, accessToken: e.target.value })}
                placeholder={settings?.hasAccessToken ? '••••••••' : 'EAABx...'}
                className="w-full px-4 py-2 pr-10 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
              />
              <button
                type="button"
                onClick={() => setShowAccessToken(!showAccessToken)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showAccessToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Business Account ID */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              <Key className="h-4 w-4 inline mr-2" />
              Business Account ID
            </label>
            <input
              type="text"
              value={formData.businessAccountId}
              onChange={(e) => setFormData({ ...formData, businessAccountId: e.target.value })}
              placeholder="987654321012345"
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Identifiant du compte WhatsApp Business
            </p>
          </div>

          {/* Enable/Disable */}
          <div className="flex items-end">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                className="w-5 h-5 rounded border-gray-300 text-cyan-500 focus:ring-cyan-500"
              />
              <span className="text-gray-700 font-medium">Activer l'envoi WhatsApp</span>
            </label>
          </div>
        </div>

        {/* Save Message */}
        {saveMessage && (
          <div
            className={`mt-6 p-4 rounded-lg ${
              saveMessage.type === 'success'
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}
          >
            <p className={saveMessage.type === 'success' ? 'text-green-800' : 'text-red-800'}>
              {saveMessage.text}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handleVerify}
            disabled={!configured || verifying}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {verifying ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Wifi className="h-4 w-4" />}
            Verifier la connexion
          </button>

          <button
            type="button"
            onClick={() => setShowTestModal(true)}
            disabled={!configured}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="h-4 w-4" />
            Envoyer un message test
          </button>

          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors flex items-center gap-2 ml-auto disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Enregistrer
          </button>
        </div>
      </form>

      {/* Test WhatsApp Modal */}
      {showTestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md m-4">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Envoyer un message WhatsApp de test</h2>
            </div>
            <div className="p-6">
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Numero du destinataire
              </label>
              <input
                type="tel"
                value={testPhoneNumber}
                onChange={(e) => setTestPhoneNumber(e.target.value)}
                placeholder="+352621123456"
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
              />
              <p className="mt-1 text-xs text-gray-500">
                Le numero doit avoir accepte de recevoir des messages de votre numero WhatsApp Business
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowTestModal(false)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSendTest}
                disabled={sendingTest || !testPhoneNumber}
                className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingTest ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Envoyer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Templates Tab Component
// ============================================================================

interface EmailTemplate {
  subject: string;
  body: string;
}

interface PushTemplate {
  title: string;
  body: string;
}

interface NotificationTemplates {
  email: Record<string, EmailTemplate>;
  sms: Record<string, string>;
  push: Record<string, PushTemplate>;
}

interface TemplateVariable {
  variable: string;
  description: string;
  example: string;
}

function TemplatesTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState<NotificationTemplates | null>(null);
  const [alertTypes, setAlertTypes] = useState<string[]>([]);
  const [alertTypeLabels, setAlertTypeLabels] = useState<Record<string, string>>({});
  const [variables, setVariables] = useState<TemplateVariable[]>([]);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Accordion states
  const [expandedChannel, setExpandedChannel] = useState<'email' | 'sms' | 'push' | null>('email');
  const [expandedAlertType, setExpandedAlertType] = useState<string | null>(null);
  const [showVariables, setShowVariables] = useState(false);

  // Preview
  const [previewChannel, setPreviewChannel] = useState<'email' | 'sms' | 'push' | null>(null);
  const [previewAlertType, setPreviewAlertType] = useState<string | null>(null);
  const [previewContent, setPreviewContent] = useState<any>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Email body tab (code vs preview) per alert type
  const [emailBodyTab, setEmailBodyTab] = useState<Record<string, 'code' | 'preview'>>({});

  // Load templates on mount
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getNotificationTemplates();
      setTemplates(response.data.templates);
      setAlertTypes(response.data.alertTypes);
      setAlertTypeLabels(response.data.alertTypeLabels);
      setVariables(response.data.variables);
    } catch (error) {
      console.error('Failed to load templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!templates) return;

    setSaving(true);
    setSaveMessage(null);

    try {
      await adminApi.updateNotificationTemplates(templates);
      setSaveMessage({ type: 'success', text: 'Templates sauvegardés avec succès' });
    } catch {
      setSaveMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Réinitialiser tous les templates aux valeurs par défaut ?')) return;

    setSaving(true);
    try {
      const response = await adminApi.resetNotificationTemplates();
      setTemplates(response.data.templates);
      setSaveMessage({ type: 'success', text: 'Templates réinitialisés' });
    } catch {
      setSaveMessage({ type: 'error', text: 'Erreur lors de la réinitialisation' });
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async (channel: 'email' | 'sms' | 'push', alertType: string) => {
    if (!templates) return;

    setLoadingPreview(true);
    setPreviewChannel(channel);
    setPreviewAlertType(alertType);

    try {
      const template = channel === 'email'
        ? templates.email[alertType]
        : channel === 'sms'
        ? templates.sms[alertType]
        : templates.push[alertType];

      const response = await adminApi.previewNotificationTemplate({
        channel,
        alertType,
        template,
      });
      setPreviewContent(response.data.preview);
    } catch (error) {
      console.error('Preview failed:', error);
    } finally {
      setLoadingPreview(false);
    }
  };

  const copyVariable = (variable: string) => {
    navigator.clipboard.writeText(variable);
  };

  const updateEmailTemplate = (alertType: string, field: 'subject' | 'body', value: string) => {
    if (!templates) return;
    setTemplates({
      ...templates,
      email: {
        ...templates.email,
        [alertType]: {
          ...templates.email[alertType],
          [field]: value,
        },
      },
    });
  };

  const updateSmsTemplate = (alertType: string, value: string) => {
    if (!templates) return;
    setTemplates({
      ...templates,
      sms: {
        ...templates.sms,
        [alertType]: value,
      },
    });
  };

  const updatePushTemplate = (alertType: string, field: 'title' | 'body', value: string) => {
    if (!templates) return;
    setTemplates({
      ...templates,
      push: {
        ...templates.push,
        [alertType]: {
          ...templates.push[alertType],
          [field]: value,
        },
      },
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  if (!templates) {
    return (
      <div className="text-center py-12 text-gray-500">
        Erreur lors du chargement des templates
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Variables Reference */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <button
          onClick={() => setShowVariables(!showVariables)}
          className="w-full flex items-center justify-between p-4 text-left"
        >
          <div className="flex items-center gap-2">
            <Info className="h-5 w-5 text-cyan-500" />
            <span className="font-medium text-gray-900">Variables disponibles</span>
            <span className="text-sm text-gray-500">({variables.length} variables)</span>
          </div>
          {showVariables ? (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400" />
          )}
        </button>

        {showVariables && (
          <div className="border-t border-gray-200 p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {variables.map((v) => (
                <div
                  key={v.variable}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm group hover:bg-gray-100"
                >
                  <div>
                    <code className="text-cyan-600 font-mono text-xs">{v.variable}</code>
                    <p className="text-gray-500 text-xs">{v.description}</p>
                  </div>
                  <button
                    onClick={() => copyVariable(v.variable)}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded"
                    title="Copier"
                  >
                    <Copy className="h-4 w-4 text-gray-400" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Email Templates */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <button
          onClick={() => setExpandedChannel(expandedChannel === 'email' ? null : 'email')}
          className="w-full flex items-center justify-between p-4 text-left"
        >
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-500" />
            <span className="font-medium text-gray-900">Templates Email</span>
          </div>
          {expandedChannel === 'email' ? (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400" />
          )}
        </button>

        {expandedChannel === 'email' && (
          <div className="border-t border-gray-200">
            {alertTypes.map((alertType) => (
              <div key={alertType} className="border-b border-gray-100 last:border-b-0">
                <button
                  onClick={() => setExpandedAlertType(expandedAlertType === `email-${alertType}` ? null : `email-${alertType}`)}
                  className="w-full flex items-center justify-between p-3 pl-8 text-left hover:bg-gray-50"
                >
                  <span className="text-sm text-gray-700">{alertTypeLabels[alertType]}</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); handlePreview('email', alertType); }}
                      className="text-xs text-cyan-600 hover:text-cyan-700 px-2 py-1 rounded hover:bg-cyan-50"
                    >
                      Aperçu
                    </button>
                    {expandedAlertType === `email-${alertType}` ? (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </button>

                {expandedAlertType === `email-${alertType}` && templates.email[alertType] && (
                  <div className="p-4 pl-8 bg-gray-50 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Sujet</label>
                      <input
                        type="text"
                        value={templates.email[alertType].subject}
                        onChange={(e) => updateEmailTemplate(alertType, 'subject', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-cyan-500 focus:border-cyan-500"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs font-medium text-gray-600">Corps (HTML)</label>
                        <div className="flex rounded-md border border-gray-300 overflow-hidden">
                          <button
                            onClick={() => setEmailBodyTab({ ...emailBodyTab, [alertType]: 'code' })}
                            className={clsx(
                              'flex items-center gap-1 px-2.5 py-1 text-xs font-medium transition-colors',
                              (emailBodyTab[alertType] || 'code') === 'code'
                                ? 'bg-cyan-600 text-white'
                                : 'bg-white text-gray-600 hover:bg-gray-50'
                            )}
                          >
                            <Code className="h-3 w-3" />
                            Code
                          </button>
                          <button
                            onClick={() => setEmailBodyTab({ ...emailBodyTab, [alertType]: 'preview' })}
                            className={clsx(
                              'flex items-center gap-1 px-2.5 py-1 text-xs font-medium border-l border-gray-300 transition-colors',
                              emailBodyTab[alertType] === 'preview'
                                ? 'bg-cyan-600 text-white'
                                : 'bg-white text-gray-600 hover:bg-gray-50'
                            )}
                          >
                            <Eye className="h-3 w-3" />
                            Visuel
                          </button>
                        </div>
                      </div>
                      {(emailBodyTab[alertType] || 'code') === 'code' ? (
                        <textarea
                          value={templates.email[alertType].body}
                          onChange={(e) => updateEmailTemplate(alertType, 'body', e.target.value)}
                          rows={8}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:ring-cyan-500 focus:border-cyan-500"
                        />
                      ) : (
                        <div
                          contentEditable
                          suppressContentEditableWarning
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(templates.email[alertType].body) }}
                          onBlur={(e) => updateEmailTemplate(alertType, 'body', e.currentTarget.innerHTML)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 overflow-auto"
                          style={{ minHeight: '250px' }}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* SMS Templates */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <button
          onClick={() => setExpandedChannel(expandedChannel === 'sms' ? null : 'sms')}
          className="w-full flex items-center justify-between p-4 text-left"
        >
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-green-500" />
            <span className="font-medium text-gray-900">Templates SMS</span>
            <span className="text-xs text-gray-400">(160 car. max recommandé)</span>
          </div>
          {expandedChannel === 'sms' ? (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400" />
          )}
        </button>

        {expandedChannel === 'sms' && (
          <div className="border-t border-gray-200">
            {alertTypes.map((alertType) => (
              <div key={alertType} className="border-b border-gray-100 last:border-b-0">
                <button
                  onClick={() => setExpandedAlertType(expandedAlertType === `sms-${alertType}` ? null : `sms-${alertType}`)}
                  className="w-full flex items-center justify-between p-3 pl-8 text-left hover:bg-gray-50"
                >
                  <span className="text-sm text-gray-700">{alertTypeLabels[alertType]}</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); handlePreview('sms', alertType); }}
                      className="text-xs text-cyan-600 hover:text-cyan-700 px-2 py-1 rounded hover:bg-cyan-50"
                    >
                      Aperçu
                    </button>
                    {expandedAlertType === `sms-${alertType}` ? (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </button>

                {expandedAlertType === `sms-${alertType}` && templates.sms[alertType] !== undefined && (
                  <div className="p-4 pl-8 bg-gray-50">
                    <div className="relative">
                      <textarea
                        value={templates.sms[alertType]}
                        onChange={(e) => updateSmsTemplate(alertType, e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-cyan-500 focus:border-cyan-500"
                      />
                      <span className={clsx(
                        'absolute bottom-2 right-2 text-xs',
                        templates.sms[alertType].length > 160 ? 'text-red-500' : 'text-gray-400'
                      )}>
                        {templates.sms[alertType].length}/160
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Push Templates */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <button
          onClick={() => setExpandedChannel(expandedChannel === 'push' ? null : 'push')}
          className="w-full flex items-center justify-between p-4 text-left"
        >
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-purple-500" />
            <span className="font-medium text-gray-900">Templates Push</span>
          </div>
          {expandedChannel === 'push' ? (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400" />
          )}
        </button>

        {expandedChannel === 'push' && (
          <div className="border-t border-gray-200">
            {alertTypes.map((alertType) => (
              <div key={alertType} className="border-b border-gray-100 last:border-b-0">
                <button
                  onClick={() => setExpandedAlertType(expandedAlertType === `push-${alertType}` ? null : `push-${alertType}`)}
                  className="w-full flex items-center justify-between p-3 pl-8 text-left hover:bg-gray-50"
                >
                  <span className="text-sm text-gray-700">{alertTypeLabels[alertType]}</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); handlePreview('push', alertType); }}
                      className="text-xs text-cyan-600 hover:text-cyan-700 px-2 py-1 rounded hover:bg-cyan-50"
                    >
                      Aperçu
                    </button>
                    {expandedAlertType === `push-${alertType}` ? (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </button>

                {expandedAlertType === `push-${alertType}` && templates.push[alertType] && (
                  <div className="p-4 pl-8 bg-gray-50 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Titre</label>
                      <input
                        type="text"
                        value={templates.push[alertType].title}
                        onChange={(e) => updatePushTemplate(alertType, 'title', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-cyan-500 focus:border-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Corps</label>
                      <input
                        type="text"
                        value={templates.push[alertType].body}
                        onChange={(e) => updatePushTemplate(alertType, 'body', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-cyan-500 focus:border-cyan-500"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewContent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-medium text-gray-900">
                Aperçu - {alertTypeLabels[previewAlertType || '']} ({previewChannel})
              </h3>
              <button
                onClick={() => { setPreviewContent(null); setPreviewChannel(null); setPreviewAlertType(null); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4">
              {loadingPreview ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-cyan-500" />
                </div>
              ) : previewChannel === 'email' ? (
                <div>
                  <div className="mb-4 p-3 bg-gray-100 rounded">
                    <span className="text-sm text-gray-500">Sujet:</span>
                    <p className="font-medium">{previewContent.subject}</p>
                  </div>
                  <div
                    className="border rounded p-4"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewContent.body) }}
                  />
                </div>
              ) : previewChannel === 'sms' ? (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-start gap-3">
                    <MessageSquare className="h-5 w-5 text-green-600 mt-0.5" />
                    <p className="text-gray-800">{previewContent}</p>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 text-right">
                    {typeof previewContent === 'string' ? previewContent.length : 0} caractères
                  </p>
                </div>
              ) : previewChannel === 'push' ? (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-start gap-3">
                    <Bell className="h-5 w-5 text-purple-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">{previewContent.title}</p>
                      <p className="text-gray-600 text-sm">{previewContent.body}</p>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleReset}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <RotateCcw className="h-4 w-4" />
          Réinitialiser par défaut
        </button>

        <div className="flex items-center gap-4">
          {saveMessage && (
            <span className={clsx(
              'text-sm',
              saveMessage.type === 'success' ? 'text-green-600' : 'text-red-600'
            )}>
              {saveMessage.text}
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 transition-colors"
          >
            {saving ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main Page Component
// ============================================================================

type TabType = 'email' | 'sms' | 'push' | 'whatsapp' | 'truphone' | 'fotaweb' | 'templates' | 'clientEmails';

export default function NotificationsSettingsPage() {
  const [activeTab, setActiveTab] = useState<TabType>('email');

  const tabs = [
    { id: 'email' as const, label: 'Email (SMTP)', icon: Mail },
    { id: 'sms' as const, label: 'SMS (Twilio)', icon: MessageSquare },
    { id: 'truphone' as const, label: 'Truphone (SMS IoT)', icon: Radio },
    { id: 'fotaweb' as const, label: 'FOTA Web (Teltonika)', icon: Upload },
    { id: 'push' as const, label: 'Push (Firebase)', icon: Bell },
    { id: 'whatsapp' as const, label: 'WhatsApp', icon: MessageCircle },
    { id: 'templates' as const, label: 'Modèles Alertes', icon: FileText },
    { id: 'clientEmails' as const, label: 'Emails Clients', icon: Palette },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <Bell className="h-7 w-7 text-cyan-500" />
          Paramètres Notifications
        </h1>
        <p className="text-gray-500 mt-1">
          Configurez les canaux de notification : Email, SMS, Push et WhatsApp
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={clsx(
                'flex items-center gap-2 pb-4 border-b-2 transition-colors',
                activeTab === tab.id
                  ? 'border-cyan-500 text-cyan-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              <tab.icon className="h-5 w-5" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'email' && <EmailTab />}
      {activeTab === 'sms' && <TwilioTab />}
      {activeTab === 'truphone' && <TruphoneTab />}
      {activeTab === 'fotaweb' && <FotaWebTab />}
      {activeTab === 'push' && <FirebaseTab />}
      {activeTab === 'whatsapp' && <WhatsAppTab />}
      {activeTab === 'templates' && <TemplatesTab />}
      {activeTab === 'clientEmails' && <ClientEmailsTab />}
    </div>
  );
}

// ============================================================================
// Client Emails Tab Component
// ============================================================================

interface ClientEmailStyle {
  logoUrl: string;
  logoWidth: number;
  logoHeight: number;
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  textColor: string;
  headerBackgroundColor: string;
  buttonColor: string;
  buttonTextColor: string;
  fontFamily: string;
  fontSize: number;
  headingFontSize: number;
  lineHeight: number;
  maxWidth: number;
  padding: number;
  borderRadius: number;
}

interface ClientEmailTemplateType {
  id: string;
  name: string;
  description: string;
  subject: string;
  htmlBody: string;
  variables: string[];
  enabled: boolean;
}

function ClientEmailsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [style, setStyle] = useState<ClientEmailStyle | null>(null);
  const [templates, setTemplates] = useState<Record<string, ClientEmailTemplateType>>({});
  const [templateTypes, setTemplateTypes] = useState<string[]>([]);
  const [templateTypeLabels, setTemplateTypeLabels] = useState<Record<string, string>>({});
  const [templateVariables, setTemplateVariables] = useState<Record<string, Array<{ variable: string; description: string }>>>({});
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // UI State
  const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState<{ subject: string; html: string } | null>(null);
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Test email
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  // Dropdown menu states
  const [showColorsMenu, setShowColorsMenu] = useState(false);
  const [showTypoMenu, setShowTypoMenu] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getClientEmailTemplates();
      setStyle(response.data.style);
      setTemplates(response.data.templates);
      setTemplateTypes(response.data.templateTypes);
      setTemplateTypeLabels(response.data.templateTypeLabels);
      setTemplateVariables(response.data.templateVariables);
    } catch (error) {
      console.error('Failed to load client email templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStyle = async () => {
    if (!style) return;

    setSaving(true);
    setSaveMessage(null);

    try {
      await adminApi.updateClientEmailStyle(style);
      setSaveMessage({ type: 'success', text: 'Style enregistré avec succès' });
    } catch {
      setSaveMessage({ type: 'error', text: 'Erreur lors de l\'enregistrement' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTemplate = async (templateId: string) => {
    const template = templates[templateId];
    if (!template) return;

    setSaving(true);
    setSaveMessage(null);

    try {
      await adminApi.updateClientEmailTemplate(templateId, {
        subject: template.subject,
        htmlBody: template.htmlBody,
        enabled: template.enabled,
      });
      setSaveMessage({ type: 'success', text: 'Template enregistré avec succès' });
    } catch {
      setSaveMessage({ type: 'error', text: 'Erreur lors de l\'enregistrement' });
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async (templateId: string) => {
    setLoadingPreview(true);
    setPreviewTemplateId(templateId);

    try {
      const response = await adminApi.previewClientEmailTemplate(templateId);
      setPreviewContent(response.data.preview);
      setShowPreview(true);
    } catch (error) {
      console.error('Preview failed:', error);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSendTest = async () => {
    if (!previewTemplateId || !testEmail) return;

    setSendingTest(true);
    try {
      await adminApi.sendTestClientEmail(previewTemplateId, testEmail);
      alert('Email de test envoyé avec succès !');
      setTestEmail('');
    } catch {
      alert('Erreur lors de l\'envoi');
    } finally {
      setSendingTest(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Réinitialiser tous les templates et styles aux valeurs par défaut ?')) return;

    setSaving(true);
    try {
      const response = await adminApi.resetClientEmailTemplates();
      setStyle(response.data.style);
      setTemplates(response.data.templates);
      setSaveMessage({ type: 'success', text: 'Templates réinitialisés' });
    } catch {
      setSaveMessage({ type: 'error', text: 'Erreur lors de la réinitialisation' });
    } finally {
      setSaving(false);
    }
  };

  const updateStyle = (key: keyof ClientEmailStyle, value: string | number) => {
    if (!style) return;
    setStyle({ ...style, [key]: value });
  };

  const updateTemplate = (templateId: string, field: keyof ClientEmailTemplateType, value: string | boolean) => {
    setTemplates({
      ...templates,
      [templateId]: {
        ...templates[templateId],
        [field]: value,
      },
    });
  };

  const copyVariable = (variable: string) => {
    navigator.clipboard.writeText(variable);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  if (!style) {
    return (
      <div className="text-center py-12 text-gray-500">
        Erreur lors du chargement des templates
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Templates List */}
      <div className="space-y-3">
        {templateTypes.map((templateId) => {
          const template = templates[templateId];
          if (!template) return null;

          const variables = templateVariables[templateId] || [];
          const isExpanded = expandedTemplate === templateId;

          return (
            <div key={templateId} className="bg-white rounded-xl shadow-sm border border-gray-200">
              <button
                onClick={() => setExpandedTemplate(isExpanded ? null : templateId)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={clsx(
                    'w-2 h-2 rounded-full',
                    template.enabled ? 'bg-green-500' : 'bg-gray-300'
                  )} />
                  <div>
                    <span className="font-medium text-gray-900">{templateTypeLabels[templateId]}</span>
                    <p className="text-xs text-gray-500">{template.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); handlePreview(templateId); }}
                    className="text-xs text-cyan-600 hover:text-cyan-700 px-3 py-1 rounded-lg hover:bg-cyan-50"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  {isExpanded ? (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </button>

              {isExpanded && (
                <div className="border-t border-gray-200">
                  {/* Toolbar */}
                  <div className="flex items-center gap-2 p-3 bg-gray-50 border-b border-gray-200 flex-wrap">
                    {/* Variables Dropdown */}
                    <div className="relative">
                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            copyVariable(e.target.value);
                            e.target.value = '';
                          }
                        }}
                        className="text-xs px-2 py-1.5 bg-white border border-gray-300 rounded cursor-pointer hover:bg-gray-50"
                        defaultValue=""
                      >
                        <option value="" disabled>Variables</option>
                        {variables.map((v) => (
                          <option key={v.variable} value={v.variable}>
                            {v.variable} - {v.description}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="w-px h-6 bg-gray-300" />

                    {/* Colors Menu */}
                    <div className="relative">
                      <button
                        onClick={() => setShowColorsMenu(!showColorsMenu)}
                        className="flex items-center gap-1 text-xs px-2 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-50"
                      >
                        <Palette className="h-3 w-3" />
                        Couleurs
                        <ChevronDown className="h-3 w-3" />
                      </button>
                      {showColorsMenu && (
                        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 p-3 min-w-[280px]">
                          <div className="grid grid-cols-2 gap-2">
                            <CompactColorInput label="Primaire" value={style.primaryColor} onChange={(v) => updateStyle('primaryColor', v)} />
                            <CompactColorInput label="Secondaire" value={style.secondaryColor} onChange={(v) => updateStyle('secondaryColor', v)} />
                            <CompactColorInput label="Fond" value={style.backgroundColor} onChange={(v) => updateStyle('backgroundColor', v)} />
                            <CompactColorInput label="Texte" value={style.textColor} onChange={(v) => updateStyle('textColor', v)} />
                            <CompactColorInput label="Bouton" value={style.buttonColor} onChange={(v) => updateStyle('buttonColor', v)} />
                            <CompactColorInput label="Texte bouton" value={style.buttonTextColor} onChange={(v) => updateStyle('buttonTextColor', v)} />
                          </div>
                          <button
                            onClick={() => { handleSaveStyle(); setShowColorsMenu(false); }}
                            className="mt-2 w-full text-xs px-2 py-1 bg-cyan-500 text-white rounded hover:bg-cyan-600"
                          >
                            Appliquer
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Typography Menu */}
                    <div className="relative">
                      <button
                        onClick={() => setShowTypoMenu(!showTypoMenu)}
                        className="flex items-center gap-1 text-xs px-2 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-50"
                      >
                        <Type className="h-3 w-3" />
                        Typographie
                        <ChevronDown className="h-3 w-3" />
                      </button>
                      {showTypoMenu && (
                        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 p-3 min-w-[250px]">
                          <div className="space-y-2">
                            <div>
                              <label className="text-xs text-gray-600">Police</label>
                              <select
                                value={style.fontFamily}
                                onChange={(e) => updateStyle('fontFamily', e.target.value)}
                                className="w-full text-xs px-2 py-1 border border-gray-300 rounded"
                              >
                                <option value="Arial, Helvetica, sans-serif">Arial</option>
                                <option value="'Helvetica Neue', Helvetica, sans-serif">Helvetica</option>
                                <option value="Georgia, serif">Georgia</option>
                                <option value="Verdana, Geneva, sans-serif">Verdana</option>
                              </select>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-xs text-gray-600">Taille</label>
                                <input
                                  type="number"
                                  value={style.fontSize}
                                  onChange={(e) => updateStyle('fontSize', parseInt(e.target.value) || 14)}
                                  className="w-full text-xs px-2 py-1 border border-gray-300 rounded"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-gray-600">Titres</label>
                                <input
                                  type="number"
                                  value={style.headingFontSize}
                                  onChange={(e) => updateStyle('headingFontSize', parseInt(e.target.value) || 24)}
                                  className="w-full text-xs px-2 py-1 border border-gray-300 rounded"
                                />
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => { handleSaveStyle(); setShowTypoMenu(false); }}
                            className="mt-2 w-full text-xs px-2 py-1 bg-cyan-500 text-white rounded hover:bg-cyan-600"
                          >
                            Appliquer
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="w-px h-6 bg-gray-300" />

                    {/* Logo URL */}
                    <div className="flex items-center gap-1 flex-1 max-w-xs">
                      <input
                        type="text"
                        value={style.logoUrl}
                        onChange={(e) => updateStyle('logoUrl', e.target.value)}
                        placeholder="URL du logo"
                        className="flex-1 text-xs px-2 py-1.5 border border-gray-300 rounded"
                      />
                    </div>

                    <div className="w-px h-6 bg-gray-300" />

                    {/* Enable Toggle */}
                    <label className="flex items-center gap-1 text-xs cursor-pointer">
                      <input
                        type="checkbox"
                        checked={template.enabled}
                        onChange={(e) => updateTemplate(templateId, 'enabled', e.target.checked)}
                        className="w-3 h-3 rounded border-gray-300 text-cyan-500 focus:ring-cyan-500"
                      />
                      Actif
                    </label>
                  </div>

                  {/* Subject */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <input
                      type="text"
                      value={template.subject}
                      onChange={(e) => updateTemplate(templateId, 'subject', e.target.value)}
                      placeholder="Sujet de l'email"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  {/* HTML Body */}
                  <div className="p-4">
                    <textarea
                      value={template.htmlBody}
                      onChange={(e) => updateTemplate(templateId, 'htmlBody', e.target.value)}
                      rows={14}
                      placeholder="Contenu HTML de l'email..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  {/* Save Button */}
                  <div className="px-4 py-3 border-t border-gray-200 flex justify-end">
                    <button
                      onClick={() => handleSaveTemplate(templateId)}
                      disabled={saving}
                      className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 transition-colors text-sm"
                    >
                      {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      Enregistrer
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Reset & Messages */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <button
            onClick={handleReset}
            disabled={saving}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
            Réinitialiser par défaut
          </button>
          {saveMessage && (
            <span className={clsx('text-sm', saveMessage.type === 'success' ? 'text-green-600' : 'text-red-600')}>
              {saveMessage.text}
            </span>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && previewContent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setShowPreview(false); setShowColorsMenu(false); setShowTypoMenu(false); }}>
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h3 className="font-medium text-gray-900">
                Aperçu - {templateTypeLabels[previewTemplateId || '']}
              </h3>
              <button
                onClick={() => { setShowPreview(false); setPreviewContent(null); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-4">
              {loadingPreview ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-6 w-6 animate-spin text-cyan-500" />
                </div>
              ) : (
                <div>
                  <div className="mb-4 p-3 bg-gray-100 rounded-lg">
                    <span className="text-sm text-gray-500">Sujet :</span>
                    <p className="font-medium text-gray-900">{previewContent.subject}</p>
                  </div>
                  <div className="border rounded-lg overflow-hidden">
                    <iframe
                      srcDoc={previewContent.html}
                      className="w-full h-[500px] bg-white"
                      title="Email Preview"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 flex items-center gap-4 flex-shrink-0">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Email de test"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
              <button
                onClick={handleSendTest}
                disabled={sendingTest || !testEmail}
                className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 transition-colors text-sm"
              >
                {sendingTest ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Envoyer test
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close menus */}
      {(showColorsMenu || showTypoMenu) && (
        <div className="fixed inset-0 z-0" onClick={() => { setShowColorsMenu(false); setShowTypoMenu(false); }} />
      )}
    </div>
  );
}

// Compact Color Input Component for toolbar
function CompactColorInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-6 h-6 rounded cursor-pointer border border-gray-300"
      />
      <span className="text-xs text-gray-600">{label}</span>
    </div>
  );
}
